import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, catchError, map, of } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { AgentConversationApiService } from '../../agent-conversations/services/agent-conversation-api.service';
import { KnowledgeMetrics } from '../../agent-conversations/models/agent-conversation.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ChatflowApiService } from '../services/chatflow-api.service';
import { ChatflowResponse } from '../models/chatflow.model';
import { extractApiErrorMessage } from '../services/chatflow-error.util';

@Component({
  selector: 'app-knowledge-metrics',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, EntitySelectComponent],
  templateUrl: './knowledge-metrics.component.html'
})
export class KnowledgeMetricsComponent {
  chatflowId: number | null = null;
  /** Nombre del chatflow elegido: el usuario nunca ve ni escribe el id (inc48). */
  chatflowName = '';
  metrics: KnowledgeMetrics | null = null;
  loading = false;
  error = '';

  constructor(
    private api: AgentConversationApiService,
    private chatflowApi: ChatflowApiService,
    private auth: AuthService
  ) {}

  /**
   * Arrow function a propósito: EntitySelectComponent la invoca como `this.searchFn(...)`
   * y un método normal perdería el `this` de este componente.
   * El endpoint de chatflows ya filtra por texto con `search`.
   */
  buscarChatflows = (term: string): Observable<EntitySelectPage<ChatflowResponse>> => {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    return this.chatflowApi.getChatflows({ businessId, search: term.trim() || undefined }).pipe(
      map((items) => ({ items, totalPages: 1 })),
      catchError(() => of({ items: [], totalPages: 0 }))
    );
  };
  etiquetaChatflow = (chatflow: ChatflowResponse) => chatflow.name;
  subEtiquetaChatflow = (chatflow: ChatflowResponse) =>
    [chatflow.chatflowType, chatflow.status].filter(Boolean).join(' · ');

  /** Al elegir un chatflow se cargan sus métricas directamente: un clic menos. */
  onChatflowSeleccionado(chatflow: ChatflowResponse | null): void {
    this.chatflowName = chatflow?.name ?? '';
    this.metrics = null;
    this.error = '';
    if (chatflow) {
      this.cargar();
    }
  }

  cargar(): void {
    if (!this.chatflowId) return;
    this.loading = true;
    this.error = '';
    this.metrics = null;

    this.api.getKnowledgeMetrics(this.chatflowId).subscribe({
      next: (data) => { this.metrics = data; this.loading = false; },
      error: (err) => {
        this.error = extractApiErrorMessage(err, 'No se pudieron cargar las métricas de Knowledge Base.');
        this.loading = false;
      }
    });
  }

  getPct(value: number): number {
    if (!this.metrics) return 0;
    const total = this.metrics.totalResolved + this.metrics.totalNotResolved + this.metrics.totalNoResults;
    return total === 0 ? 0 : Math.round((value / total) * 100);
  }
}
